// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CheckboxList.tsx
 * @input Uses React, useId, useOptimistic, useTransition, Field, List, CheckboxListContext
 * @output Exports CheckboxList component, CheckboxListProps
 * @position Core implementation; consumed by index.ts, tested by CheckboxList.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/CheckboxList/CheckboxList.doc.mjs
 * - /packages/core/src/CheckboxList/CheckboxList.test.tsx
 * - /packages/core/src/CheckboxList/index.ts
 * - /apps/storybook/stories/CheckboxList.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/CheckboxList/ (showcase blocks)
 */
import { useCallback, useId, useMemo, useOptimistic, useTransition } from 'react';
import { Field } from "../Field/Field.js";
import { List } from "../List/List.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { CheckboxListContext } from "./CheckboxListContext.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_ARRAY = [];
/**
 * A checkbox group component for multi-value selection.
 *
 * Composes Field (for label, description, status) and List
 * (for density, dividers) with a context provider for collection mode.
 *
 * @example
 * ```
 * <CheckboxList
 *   label="Notifications"
 *   value={selected}
 *   onChange={setSelected}>
 *   <CheckboxListItem label="Email" value="email" />
 *   <CheckboxListItem label="SMS" value="sms" />
 *   <CheckboxListItem label="Push" value="push" />
 * </CheckboxList>
 * ```
 */
export function CheckboxList({
  label,
  isLabelHidden = false,
  description,
  status,
  value,
  onChange,
  changeAction,
  density = 'balanced',
  hasDividers = false,
  isDisabled = false,
  disabledMessage,
  isReadOnly = false,
  children,
  ref,
  width,
  xstyle,
  className,
  style,
  'data-testid': dataTestId,
  ...rest
}) {
  const inputID = useId();
  const labelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const isCollectionMode = value !== undefined;
  const effectiveValue = value ?? EMPTY_ARRAY;
  const [optimisticValue, setOptimisticValue] = useOptimistic(effectiveValue);
  // Tracks which item has a pending `changeAction`. Auto-reverts to null when
  // the transition settles, so the spinner clears without manual cleanup.
  const [loadingValue, setLoadingValue] = useOptimistic(null);

  // Disabled-reason tooltip. Applies to the whole-group disabled state. Disabled
  // controls swallow pointer events, so the tooltip listeners attach to the
  // group container and the checkboxes stay perceivable via aria-disabled
  // instead of the disabled attribute. Toggling is blocked in the item.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The group container is not naturally focusable; focusin bubbles up from
    // the checkboxes, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const handleChange = useCallback((newValues, toggledValue) => {
    onChange?.(newValues);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValues);
        if (toggledValue !== undefined) {
          setLoadingValue(toggledValue);
        }
        await changeAction(newValues);
      });
    }
  }, [onChange, changeAction, startTransition, setOptimisticValue, setLoadingValue]);
  const contextValue = useMemo(() => ({
    value: isCollectionMode ? optimisticValue : undefined,
    onChange: isCollectionMode ? handleChange : undefined,
    isDisabled,
    hasDisabledMessage: showsDisabledMessage,
    isReadOnly,
    loadingValue
  }), [isCollectionMode, optimisticValue, handleChange, isDisabled, showsDisabledMessage, isReadOnly, loadingValue]);
  return /*#__PURE__*/_jsxs(Field, {
    ...rest,
    ref: ref,
    "data-testid": dataTestId,
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: inputID,
    labelID: labelID,
    isGroupLabel: true,
    descriptionID: description ? descriptionID : undefined,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    statusVariant: "detached",
    width: width,
    xstyle: xstyle,
    ...mergeProps(themeProps('checkbox-list'), {
      className,
      style
    }),
    children: [/*#__PURE__*/_jsx(CheckboxListContext, {
      value: contextValue,
      children: /*#__PURE__*/_jsx("div", {
        ref: el => {
          // Anchor + hover/focus listeners for the disabled-message tooltip.
          // Handlers are gated internally by isEnabled, so attaching
          // unconditionally is safe.
          disabledMessageTooltip.ref(el);
        },
        role: "group",
        "aria-labelledby": labelID,
        "aria-describedby": [description ? descriptionID : null, status?.message ? statusMessageID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined,
        children: /*#__PURE__*/_jsx(List, {
          density: density,
          hasDividers: hasDividers,
          children: children
        })
      })
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
CheckboxList.displayName = 'CheckboxList';